import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Share2, Trash2, Pencil, FileText } from 'lucide-react';
import { MOOD_STYLE, MOOD_FALLBACK } from './MoodPicker';

const ShareBox = ({ entry, channels, onShared }) => {
  const [channel, setChannel] = useState(channels[0]?.id || '');
  const [anonymous, setAnonymous] = useState(true);
  const [busy, setBusy] = useState(false);

  const share = async () => {
    if (!channel) return;
    setBusy(true);
    try {
      await axios.post(`${API}/companion/journal/${entry.id}/share`, {
        category_id: channel, anonymous,
      });
      toast.success(anonymous ? 'Shared as “A member”' : 'Shared under your name');
      onShared();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'That was not shared');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-black/25 p-3 space-y-3"
         data-testid={`share-box-${entry.id}`}>
      <p className="text-xs text-white/50">
        Post this into a chat. Everyone in that channel will be able to read it.
      </p>
      <select
        value={channel}
        onChange={(e) => setChannel(e.target.value)}
        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm
                   text-white focus:outline-none focus:border-cyan-400/60"
        data-testid={`share-channel-${entry.id}`}
      >
        {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
        <input
          type="checkbox"
          checked={anonymous}
          onChange={(e) => setAnonymous(e.target.checked)}
          className="accent-cyan-400"
          data-testid={`share-anon-${entry.id}`}
        />
        Post it anonymously, as “A member”
      </label>
      <button
        onClick={share}
        disabled={busy || !channel}
        className="rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 px-4 py-1.5
                   text-xs text-white transition-colors duration-200"
        data-testid={`share-confirm-${entry.id}`}
      >
        {busy ? 'Sharing…' : 'Share it'}
      </button>
    </div>
  );
};

/** One entry: free writing, a prompt they answered, or a worksheet they kept private. */
export const JournalEntryRow = ({ entry, moods, channels, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [body, setBody] = useState(entry.body || '');
  const Chevron = open ? ChevronDown : ChevronRight;
  const fromWorksheet = entry.source === 'worksheet';
  const labels = moods || MOOD_FALLBACK;

  const save = async () => {
    try {
      await axios.patch(`${API}/companion/journal/${entry.id}`, { body });
      setEditing(false);
      toast.success('Entry updated');
      onChanged();
    } catch {
      toast.error('That change was not saved');
    }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API}/companion/journal/${entry.id}`);
      toast.success('Entry removed');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'That entry was not removed');
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden"
         data-testid={`journal-entry-${entry.id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-3 px-4 py-3 text-left
                   hover:bg-white/[0.04] transition-colors duration-200"
        data-testid={`journal-entry-toggle-${entry.id}`}
      >
        <span className="flex items-start gap-2 min-w-0">
          <Chevron className="w-4 h-4 text-white/40 shrink-0 mt-0.5" />
          <span className="min-w-0">
            <span className="block text-sm text-white/90 truncate">
              {entry.title || 'Reflection'}
            </span>
            <span className="block text-xs text-white/40 mt-0.5 truncate">
              {new Date(entry.created_at).toLocaleDateString(undefined,
                { weekday: 'short', month: 'short', day: 'numeric' })}
              {entry.content_title ? ` · ${entry.content_title}` : ''}
              {fromWorksheet ? ' · from a worksheet' : ''}
            </span>
          </span>
        </span>
        <span className="shrink-0 flex items-center gap-2">
          {entry.mood && (
            <span className={`${MOOD_STYLE[entry.mood].fill} rounded-full w-2.5 h-2.5`}
                  title={labels[entry.mood]} />
          )}
          {entry.shared_post_id && (
            <span className="text-[10px] text-violet-200/70" data-testid={`journal-shared-${entry.id}`}>
              shared
            </span>
          )}
        </span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3" data-testid={`journal-entry-detail-${entry.id}`}>
          {entry.prompt_text && (
            <p className="text-xs text-white/55 italic">{entry.prompt_text}</p>
          )}

          {editing ? (
            <>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm
                           text-white focus:outline-none focus:border-cyan-400/60"
                data-testid={`journal-edit-body-${entry.id}`}
              />
              <div className="flex gap-2">
                <button onClick={save}
                  className="rounded-full bg-cyan-400/20 border border-cyan-300/40 px-4 py-1.5
                             text-xs text-cyan-50 hover:bg-cyan-400/30 transition-colors duration-200"
                  data-testid={`journal-edit-save-${entry.id}`}>Save</button>
                <button onClick={() => { setEditing(false); setBody(entry.body || ''); }}
                  className="rounded-full border border-white/10 px-4 py-1.5 text-xs text-white/60
                             hover:text-white transition-colors duration-200"
                  data-testid={`journal-edit-cancel-${entry.id}`}>Cancel</button>
              </div>
            </>
          ) : (
            <p className="text-sm text-white/80 whitespace-pre-line leading-relaxed">
              {entry.body}
            </p>
          )}

          {sharing && (
            <ShareBox entry={entry} channels={channels}
              onShared={() => { setSharing(false); onChanged(); }} />
          )}

          <div className="flex flex-wrap items-center gap-4 pt-1">
            {!editing && !fromWorksheet && (
              <button onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white
                           transition-colors duration-200"
                data-testid={`journal-edit-${entry.id}`}>
                <Pencil className="w-3 h-3" /> Edit
              </button>
            )}
            {channels.length > 0 && (
              <button onClick={() => setSharing(!sharing)}
                className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white
                           transition-colors duration-200"
                data-testid={`journal-share-${entry.id}`}>
                <Share2 className="w-3 h-3" /> {entry.shared_post_id ? 'Share again' : 'Share to a chat'}
              </button>
            )}
            {fromWorksheet && entry.module_id && (
              <Link to={`/dashboard/modules/${entry.module_id}`}
                className="inline-flex items-center gap-1.5 text-xs text-cyan-300 hover:text-cyan-200"
                data-testid={`journal-worksheet-${entry.id}`}>
                <FileText className="w-3 h-3" /> Open the worksheet
              </Link>
            )}
            {!fromWorksheet && (
              <button onClick={remove}
                className="inline-flex items-center gap-1.5 text-xs text-white/30 hover:text-rose-300
                           transition-colors duration-200 ml-auto"
                data-testid={`journal-delete-${entry.id}`}>
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalEntryRow;
